import React, { useState, useEffect, useContext, useMemo } from "react";
import { GlobalContext } from "./GlobalContext";
import { AddEditCommitted } from "./lazyForms";
import CommonDataView from "./CommonDataView";
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import MessageSelected from "./MessageSelected";
import { Button } from "@radix-ui/themes";
import FloatingDownloadButton from "./FloatingDownloadButton";
import CollectionTargetBar from "./CollectionTargetBar";
import DensityToggle from "./components/DensityToggle";
import { formatUGX } from "./UtilityFunctions";

function Credit({ cars, setCars, initialSelectedRow = "" , isLoading = false }){
    const ownSheetName = "Credit";

    const {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(cars, setCars, ownSheetName);    

    return (
        <div className="flex flex-col h-full sheet-viewport" id="credit-content">
            {/* Above the scroll container, not inside it: that container scrolls
                horizontally with the table, so chips placed within it would slide
                off the side exactly like the column header they replace. Desktop
                only — MobileSheetView renders the same component in its toolbar. */}
            <SheetFilterChips
                filterObject={filterObject}
                filterData={filterData}
                className="hidden md:flex"
            />
            <Content isLoading={isLoading}
                modalVisible={modalVisible}
                setModalVisibility={setModalVisibility}
                filteredDataArray={filteredDataArray} setCars={setCars} sortData={sortData}
                sortOrder={sortOrder} parentSheetName={ownSheetName}
                filterData={filterData} filterObject={filterObject}
                ownSheetName={ownSheetName}
                initialSelectedRow={initialSelectedRow}
            />
            <Summary 
              setModalVisibility={setModalVisibility} 
              filteredDataArray={filteredDataArray} 
              setMessagesModalVisible={setMessagesModalVisible}/>
            {
              messagesModalVisible && 
                  <MessageSelected
                      onClose={()=>{setMessagesModalVisible(false)}}
                      recordsData={filteredDataArray}
                      sheetName={ownSheetName}
                  />
            }
        </div>
    )
}

function Content({ filteredDataArray, setCars, modalVisible, setModalVisibility,
   sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, initialSelectedRow = "", isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Make', 'Model', 'ModelYear', 'CustomerName', 'TotalBalance', 'DueDate']);
    
    const [ currentVehicleIndex, setCurrentVehicle ] = useState();
    const openModalEdit = (vehicleId)=>{
        setCurrentVehicle(vehicleId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }

    const [ selectedRow, setSelectedRow ] = useState(initialSelectedRow)

    useEffect(() => {
        if (initialSelectedRow) setSelectedRow(initialSelectedRow);
    }, [filteredDataArray, initialSelectedRow]);

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    // Closures reference no component state — stable for the component lifetime.
    const formattingRules = useMemo(() => ({
        Days: {
            formatByValue: (val)=>{
                if(val > 240 && val <= 270){
                    return {color: "orange", fontWeight: "bold"}
                }
                if(val > 270){
                    return {color: "red", fontWeight: "bold"}
                }
                return {color: "green", fontWeight: "bold"}
            }
        },
        DaysCommitted: {
            formatByValue: (val)=>{
                if(val > 30 && val <= 60){
                    return {color: "orange", fontWeight: "bold", textAlign: "center"}
                }
                if(val > 60){
                    return {color: "red", fontWeight: "bold", textAlign: "center"}
                }
                return {color: "green", fontWeight: "bold", textAlign: "center"}
            }
        }
    }), []);

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"VehicleID"}
                formattingRules={formattingRules}
                searchPlaceholder="Search credit sales…"
            />
            {
                modalVisible.visible &&  filteredDataArray[currentVehicleIndex] &&
                <AddEditCommitted 
                    formMode={modalVisible.mode} 
                    showModal={setModalVisibility} 
                    car={filteredDataArray[currentVehicleIndex]} 
                    parentSheetName={parentSheetName} 
                    //In add mode, read the origin that the user selected. In edit mode, read the origin from the record's origin field
                    origin={modalVisible.mode==="add" ? modalVisible.origin : filteredDataArray[currentVehicleIndex].Origin}
                    //The sheet as the user currently has it filtered and sorted.
                    //It gives the header its record stepper and its finder, so
                    //reaching the next deal no longer means closing the modal.
                    fleet={filteredDataArray}
                    fleetIndex={currentVehicleIndex}
                    onStep={setCurrentVehicle}
                />    
            }
            <FloatingDownloadButton 
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                filterObject={filterObject}
            />     
        </div>
    )
}

function Summary({ filteredDataArray, setMessagesModalVisible }){
    const { can } = useContext(GlobalContext);
    return (
        /* sheet-actions-bar: same fixed-height non-wrapping row as Inventory's,
           and at 412px Send Messages, the collection bar and the stats were
           squeezing each other rather than overflowing — the collection bar's
           three labels ended up crushed into half a screen. Letting the row
           take its natural width and scroll is the same bargain made there. */
        <div className="h-14 md:h-[80px] flex-none bg-white border-t flex gap-4 items-center px-4 sheet-actions-bar" id="credit-summary">
            {can('messages.send') && <Button
                id="message-filtered-button"
                className="ml-2"
                variant="soft"
                size="2"
                radius="medium"
                //onClick={()=>{setMessagesModalVisible(true)}}
                onClick={()=>{setMessagesModalVisible(true)}}
                title="Send messages to all customers in the current filtered list"
            >Send Messages</Button>}
            {/* Unlike the stat cards beside it, this one is dealership-wide and
                ignores the sheet's filters — see the header of
                CollectionTargetBar.jsx for why it has to be. */}
            <CollectionTargetBar />
            <CreditSalesStatsBar
              className="border-none shadow-none ml-2"
              filteredDataArray={filteredDataArray} />
            {/* Pinned right, as on Inventory and Committed — see the note in
                Inventory.jsx for why these three needed it adding by hand. */}
            <div className="hidden md:flex items-center ml-auto pr-1">
                <DensityToggle id="credit-density-toggle" />
            </div>
        </div>
    )
}

import { Card, Metric, Text, Flex, Grid } from "@tremor/react";
import SheetFilterChips from "./components/SheetFilterChips";

function CreditSalesStatsBar({ filteredDataArray }) {
  const [stats, setStats] = useState({
    totalVehicles: 0,
    totalValue: 0,
    totalPaid: 0,
    totalBalance: 0,
    overdueCount: 0,
    totalOverduePenalties: 0,
    totalTrackerCost: 0
  });

  useEffect(() => {
    if (!filteredDataArray || filteredDataArray.length === 0) return;

    // One pass, not six. This used to run five reduces and a filter over the same
    // array, so every keystroke in the search box walked the credit book six
    // times; on a large sheet that is the difference between one traversal and
    // six for numbers that are all read off the same row.
    //
    // Number() on every figure is not decoration: mysql2 returns DECIMAL as a
    // STRING, and a NULL numeric reaches the sheet as "" (normalizeValue in
    // UtilityFunctions.jsx turns it into one, so it is "" here rather than null).
    // Number("") is 0 and Number(null) is 0, so a deal with no recorded tracker
    // fee contributes nothing to the total instead of poisoning it with NaN —
    // which is right, because "no fee recorded" is not a cost of zero, it is the
    // absence of a figure, and the SHEET CELL still shows it blank.
    const totals = filteredDataArray.reduce((acc, car) => {
      acc.totalValue += Number(car.AgreedPrice) || 0;
      acc.totalPaid += Number(car.TotalPaid) || 0;
      acc.totalBalance += Number(car.TotalBalance) || 0;
      acc.totalOverduePenalties += Number(car.TotalPenalty) || 0;
      acc.totalTrackerCost += Number(car.TotalTrackerCost) || 0;
      if ((car.DaysOverdue || 0) > 0) acc.overdueCount += 1;
      return acc;
    }, {
      totalValue: 0, totalPaid: 0, totalBalance: 0,
      totalOverduePenalties: 0, totalTrackerCost: 0, overdueCount: 0,
    });

    setStats({ totalVehicles: filteredDataArray.length, ...totals });
  }, [filteredDataArray]);

  //Shared formatter — see formatUGX in UtilityFunctions.jsx.
  const formatCurrency = (value) => formatUGX(value);

  return (
    //Stats cards overflow their band on phones — desktop only for now
    <div className="bg-white rounded-md h-18 hidden md:block" id="credit-stats">
      <Grid numItemsSm={3} numItemsLg={6} className="gap-2 h-full">
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Total Number</Text>
          <Metric className="text-lg">{stats.totalVehicles}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Outstanding</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalBalance)}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500 flex items-center">
            Overdue Customers
            {stats.overdueCount > 0 && (
              <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </Text>
          <Metric className={`text-lg ${stats.overdueCount > 0 ? 'text-red-500' : ''}`}>
            {stats.overdueCount}
          </Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Penalties</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalOverduePenalties)}</Metric>
        </Card>
        {/* What the trackers on these deals have cost so far — the fifth of six
            grid slots, so nothing reflows. This is money already spent, not owed
            to us, which is why it reads "Tracking Cost" rather than a balance. */}
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Tracking Cost</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalTrackerCost)}</Metric>
        </Card>
      </Grid>
    </div>
  );
}

export default Credit;